import type { GroupedIssueItem, IssueGrouping } from "./groupAndSort";

/**
 * Where issues sit, top to bottom, inside a band of the Gantt or a column of
 * the board — and the shared arrangement that decides it.
 *
 * Two sources of order, in this precedence:
 *
 *   1. MANUAL. Positions someone set by dragging, stored per band in
 *      `pm_issue_placements` and shared by everyone on the project. A drop
 *      records the order of the whole band as it was seen, so every issue that
 *      was on screen keeps its place until someone moves it again.
 *   2. AUTOMATIC. Everything without a stored position. On the Gantt, issues
 *      joined by a dependency are pulled next to each other, so an arrow spans
 *      a row or two instead of the whole chart. On the board, where there are
 *      no arrows, it is simply the order the issues arrived in.
 *
 * An issue that joins an arranged band — created later, moved in from another
 * status, or never placed — is slotted beside a dependency partner already in
 * the band rather than dropped at the bottom. Arranged issues never move to
 * make room for a dependency: the manual order wins.
 *
 * Everything here is pure; the Gantt and the board feed it what they render.
 */

export type PlacementLayout = "board" | "gantt";

export interface IssuePlacementRow {
  issue_id: number;
  layout: PlacementLayout;
  group_by: string;
  group_key: string;
  position: number;
}

/** A dependency edge, reduced to the two ends arrangement cares about. */
export interface PlacementEdge {
  predecessor_issue_id: number;
  successor_issue_id: number;
}

/** issue id → stored position within one band. */
export type BandRanks = ReadonlyMap<number, number>;

/** Append to the list under `key`, creating it on first use. */
function pushTo<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}

/** Group the rows of one (layout, group_by) by band. */
export function indexPlacements(
  rows: readonly IssuePlacementRow[]
): Map<string, Map<number, number>> {
  const bands = new Map<string, Map<number, number>>();
  for (const row of rows) {
    let band = bands.get(row.group_key);
    if (!band) {
      band = new Map();
      bands.set(row.group_key, band);
    }
    band.set(row.issue_id, row.position);
  }
  return bands;
}

/** A band's stored arrangement as an ordered list of issue ids. */
export function storedSequence(ranks: BandRanks | undefined): number[] {
  if (!ranks) return [];
  // Ties are broken by id so the sequence is deterministic even if two writers
  // raced and left two issues on one position.
  return [...ranks.entries()].sort((a, b) => a[1] - b[1] || a[0] - b[0]).map(([id]) => id);
}

/**
 * Dependency clustering over one band: connected issues grouped together,
 * predecessors above successors, everything else in `baseOrder`.
 *
 * Clusters are placed where their earliest member would have been, so an
 * issue with no dependencies stays exactly where the base order put it. Inside
 * a cluster the order is topological, preferring base order wherever the
 * dependencies leave a choice. A cycle cannot come from the database (the
 * trigger refuses one), but a staged edit is not checked yet — anything a
 * cycle leaves unplaced keeps its base order rather than disappearing.
 */
function clusterOrder(baseOrder: readonly number[], edges: readonly PlacementEdge[]): number[] {
  const basePos = new Map(baseOrder.map((id, index) => [id, index]));
  const predecessors = new Map<number, number[]>();
  const successors = new Map<number, number[]>();
  const parent = new Map(baseOrder.map((id) => [id, id]));

  const find = (id: number): number => {
    let root = id;
    while (parent.get(root) !== root) root = parent.get(root) as number;
    // Path compression, so long chains stay cheap on a big band.
    let node = id;
    while (node !== root) {
      const next = parent.get(node) as number;
      parent.set(node, root);
      node = next;
    }
    return root;
  };

  for (const edge of edges) {
    const from = edge.predecessor_issue_id;
    const to = edge.successor_issue_id;
    if (from === to || !basePos.has(from) || !basePos.has(to)) continue;
    pushTo(successors, from, to);
    pushTo(predecessors, to, from);
    const a = find(from);
    const b = find(to);
    if (a !== b) {
      // The root with the earlier base position wins, so a cluster's root is
      // always its earliest member — which is where the cluster is placed.
      if ((basePos.get(a) as number) < (basePos.get(b) as number)) parent.set(b, a);
      else parent.set(a, b);
    }
  }

  const clusters = new Map<number, number[]>();
  for (const id of baseOrder) {
    const root = find(id);
    pushTo(clusters, root, id);
  }

  const ordered: number[] = [];
  const emitted = new Set<number>();
  for (const id of baseOrder) {
    const root = find(id);
    if (root !== id) continue;
    const members = clusters.get(root) ?? [id];
    const remaining = new Map(
      members.map((m) => [m, (predecessors.get(m) ?? []).filter((p) => p !== m).length])
    );
    // Kahn's algorithm, taking the earliest ready member each time. A band is a
    // few hundred issues at most, so a linear scan beats a heap on clarity.
    while (remaining.size > 0) {
      let next: number | null = null;
      for (const [member, blockers] of remaining) {
        if (blockers > 0) continue;
        if (next === null || (basePos.get(member) as number) < (basePos.get(next) as number)) {
          next = member;
        }
      }
      if (next === null) break;
      remaining.delete(next);
      ordered.push(next);
      emitted.add(next);
      for (const successor of successors.get(next) ?? []) {
        const blockers = remaining.get(successor);
        if (blockers !== undefined) remaining.set(successor, blockers - 1);
      }
    }
    for (const member of members) {
      if (!emitted.has(member)) {
        ordered.push(member);
        emitted.add(member);
      }
    }
  }
  return ordered;
}

/**
 * The display order of one band.
 *
 * @param baseOrder every id in the band, in the order to fall back on
 * @param ranks     the band's stored positions (manual placement)
 * @param edges     dependencies to cluster by, or null for no clustering
 */
export function arrangeIds(
  baseOrder: readonly number[],
  ranks: BandRanks | undefined,
  edges: readonly PlacementEdge[] | null
): number[] {
  const auto = edges ? clusterOrder(baseOrder, edges) : [...baseOrder];
  const basePos = new Map(baseOrder.map((id, index) => [id, index]));
  const placed = baseOrder
    .filter((id) => ranks?.has(id))
    .sort(
      (a, b) =>
        (ranks?.get(a) as number) - (ranks?.get(b) as number) ||
        (basePos.get(a) as number) - (basePos.get(b) as number)
    );
  if (placed.length === 0) return auto;

  const result = [...placed];
  const inResult = new Set(result);
  // Everything slotted in beside an anchor is tracked against that anchor's
  // root, so a second issue attached after the same anchor lands after the
  // first one (and its followers) rather than squeezing in between them.
  const rootOf = new Map(placed.map((id) => [id, id]));
  const tailOf = new Map(placed.map((id) => [id, id]));

  const predecessorsOf = new Map<number, number[]>();
  const successorsOf = new Map<number, number[]>();
  for (const edge of edges ?? []) {
    const from = edge.predecessor_issue_id;
    const to = edge.successor_issue_id;
    if (from === to || !basePos.has(from) || !basePos.has(to)) continue;
    pushTo(predecessorsOf, to, from);
    pushTo(successorsOf, from, to);
  }

  for (const id of auto) {
    if (inResult.has(id)) continue;
    const placedPredecessors = (predecessorsOf.get(id) ?? []).filter((p) => inResult.has(p));
    const placedSuccessors = (successorsOf.get(id) ?? []).filter((s) => inResult.has(s));

    if (placedPredecessors.length > 0) {
      // Below the LOWEST predecessor already on screen, after anything that
      // was attached to it before.
      const anchor = placedPredecessors.reduce((a, b) =>
        result.indexOf(a) >= result.indexOf(b) ? a : b
      );
      const root = rootOf.get(anchor) ?? anchor;
      const tail = tailOf.get(root) ?? anchor;
      const at = Math.max(result.indexOf(anchor), result.indexOf(tail)) + 1;
      result.splice(at, 0, id);
      rootOf.set(id, root);
      tailOf.set(root, id);
    } else if (placedSuccessors.length > 0) {
      // Above the HIGHEST successor on screen.
      const anchor = placedSuccessors.reduce((a, b) =>
        result.indexOf(a) <= result.indexOf(b) ? a : b
      );
      result.splice(result.indexOf(anchor), 0, id);
      rootOf.set(id, id);
      tailOf.set(id, id);
    } else {
      result.push(id);
      rootOf.set(id, id);
      tailOf.set(id, id);
    }
    inResult.add(id);
  }
  return result;
}

/**
 * One band's rows split into what moves together: a top-level row and the
 * subissues `groupAndSortIssues` glued beneath it.
 */
export interface PlacementUnit {
  head: GroupedIssueItem;
  children: GroupedIssueItem[];
}

export function unitsOf(items: readonly GroupedIssueItem[]): PlacementUnit[] {
  const units: PlacementUnit[] = [];
  for (const item of items) {
    const last = units[units.length - 1];
    if (item.indentLevel > 0 && last) last.children.push(item);
    else units.push({ head: item, children: [] });
  }
  return units;
}

/**
 * Apply the arrangement to grouped rows.
 *
 * Top-level rows are arranged as units, carrying their subissues with them;
 * a unit's dependencies are its members' dependencies, so a subissue linked to
 * another issue still pulls its parent's block towards it. Subissues are then
 * arranged among their siblings, from the same band's stored positions (the
 * two sets share a band's numbering but are only ever compared within
 * themselves).
 */
export function arrangeGroups(
  groups: readonly IssueGrouping[],
  ranksFor: (groupKey: string) => BandRanks | undefined,
  edges: readonly PlacementEdge[] | null
): IssueGrouping[] {
  return groups.map((group) => {
    const ranks = ranksFor(group.key);
    const units = unitsOf(group.items);
    const unitOf = new Map<number, number>();
    for (const unit of units) {
      unitOf.set(unit.head.issue.id, unit.head.issue.id);
      for (const child of unit.children) unitOf.set(child.issue.id, unit.head.issue.id);
    }
    const unitEdges = edges
      ? edges.flatMap((edge) => {
          const from = unitOf.get(edge.predecessor_issue_id);
          const to = unitOf.get(edge.successor_issue_id);
          return from !== undefined && to !== undefined && from !== to
            ? [{ predecessor_issue_id: from, successor_issue_id: to }]
            : [];
        })
      : null;

    const byHead = new Map(units.map((unit) => [unit.head.issue.id, unit]));
    const headOrder = arrangeIds(
      units.map((unit) => unit.head.issue.id),
      ranks,
      unitEdges
    );

    const items: GroupedIssueItem[] = [];
    for (const headId of headOrder) {
      const unit = byHead.get(headId);
      if (!unit) continue;
      items.push(unit.head);
      if (unit.children.length === 0) continue;
      const childById = new Map(unit.children.map((child) => [child.issue.id, child]));
      const childOrder = arrangeIds(
        unit.children.map((child) => child.issue.id),
        ranks,
        edges
      );
      for (const childId of childOrder) {
        const child = childById.get(childId);
        if (child) items.push(child);
      }
    }
    return { ...group, items };
  });
}

/**
 * The full order to store for a band after a drop.
 *
 * `visible` is the new order of what the user can see. Issues stored for the
 * band but not on screen (filtered out, or subissues when the heads were
 * rearranged) keep their place relative to the visible issue they followed,
 * so arranging a filtered view never scrambles what the filter hid.
 */
export function mergeArrangement(stored: readonly number[], visible: readonly number[]): number[] {
  const visibleSet = new Set(visible);
  const trailing = new Map<number | null, number[]>();
  let anchor: number | null = null;
  for (const id of stored) {
    if (visibleSet.has(id)) {
      anchor = id;
      continue;
    }
    pushTo(trailing, anchor, id);
  }
  const merged = [...(trailing.get(null) ?? [])];
  for (const id of visible) {
    merged.push(id, ...(trailing.get(id) ?? []));
  }
  return merged;
}

/**
 * The rows a save has to write: every id whose position differs from what is
 * stored. Unchanged rows are left alone so one drop does not rewrite, and
 * re-broadcast, a whole band.
 */
export function changedPositions(
  order: readonly number[],
  ranks: BandRanks | undefined
): { issue_id: number; position: number }[] {
  return order.flatMap((id, position) =>
    ranks?.get(id) === position ? [] : [{ issue_id: id, position }]
  );
}

/** Move `movedId` to `index` in `order` (index counted without `movedId`). */
export function moveTo(order: readonly number[], movedId: number, index: number): number[] {
  const without = order.filter((id) => id !== movedId);
  const at = Math.max(0, Math.min(index, without.length));
  return [...without.slice(0, at), movedId, ...without.slice(at)];
}
